import { 
	merge, 
	set 
} from "lodash";

import {
	ComponentTranslationBundle, 
	i18NextJson, 
	LanguageSpecifier 
} from "../i18nService";

type AgencyTypeDto = {
    Code: string;
    Name: string;
    Translations?: ComponentTranslationBundle;
}

type FormDto = {
    Name: string;
	Agency: {
		Types: AgencyTypeDto[];
	}
    nomenclature: Record<string, string>;
    Nomenclature: Record<string, string>;
    Translations?: ComponentTranslationBundle;
}

const nomenclatureTranslationKeyMap = {
	"PositiveReport": "reporting.types.POSITIVE.name",
	"NegativeReport": "reporting.types.NEGATIVE.name",
	"ReportFooter": "reporting.types.POSITIVE.footer",
	"PrimaryEntity": "meta.primary.entity.name",
	"PrimaryDate": "meta.primary.date.name",
	"FormUseAcceptance": "meta.acceptance",
	"SupportEmail": "feedback.email",
	"SupportNumber": "feedback.phoneNumber",
	"SupportMarkdown": "feedback.markdown",
	"FORM.REPORTING.POSITIVE": "reporting.types.POSITIVE.name",
	"FORM.REPORTING.NEGATIVE": "reporting.types.NEGATIVE.name",
	"FORM.REPORTING.FOOTER": "reporting.types.POSITIVE.footer",
	"FORM.PRIMARY.ENTITY": "meta.primary.entity.name",
	"FORM.PRIMARY.DATE": "meta.primary.date.name",
	"FORM.ACCEPTANCE": "meta.acceptance",
	"FORM.SUPPORT.EMAIL": "feedback.email",
	"FORM.SUPPORT.PHONE": "feedback.phoneNumber",
	"FORM.SUPPORT.MARKDOWN": "feedback.markdown"
};

export const buildFormTranslations = (
	form: FormDto,
	language: LanguageSpecifier,
	isDefault: boolean
): i18NextJson => {
	//we want to construct an object that looks like this:
	/*
		{
			"name": "Form Name" : from the FormDto.translations,
			"types": {
				"CODE": {
					"name": "Type Code" : from the AgencyTypeDto.translations if defined, else type.Name
					//should use the getTypeTranslations function
				}
			}
		}
		*/

	const nomenclatureTranslations = mapNomenclatureToTranslations({
		...form.nomenclature,
		...form.Nomenclature
	});
	
	const typesTranslations = getTypesTranslations(form, language, isDefault);
	const translations: Record<string, string> = merge(
		{},
		(isDefault ? {
			name: form.Name
		} : {}),
		nomenclatureTranslations,
		(form.Translations?.[language] ?? {}),
		typesTranslations
	);
	
	//now, some of the trannslations keys may be stringified object representations
	//similar to types.DEALER.{ "code": "something" }
	//we need to convert this to an actual object
	//we do also have access to lodash, so we can probably use that:
	
	const filtered = Object.entries(translations)
		.filter(([key, value]) => !key.includes(".") || !!value)
		.map(e => e[0]);
	
	const final = {};
	for(const key of filtered) {
		set(final, key, translations[key]);
	}

	return final;
};

function mapNomenclatureToTranslations(nomenclature: Record<string, (string | { text: string })>): Record<string, string> {
	const translations = Object.entries(nomenclatureTranslationKeyMap).reduce((acc, [nextKey, nextVal]) => {
		if(nomenclature[nextKey] !== undefined) {
			acc[nextVal] = (nomenclature[nextKey] as { text: string }).text ?? nomenclature[nextKey];
		}

		return acc;
	}, {});

	return translations;
}
	
function getTypesTranslations(
	form: FormDto,
	language: LanguageSpecifier,
	isDefault: boolean
) {
	const typeTranslations = Object.values(form.Agency.Types)
		.reduce((acc, type) => {
			const translations = getTypeTranslations(type, language, isDefault);
	
			if(translations) {
				acc[getTypeKeyPrefix(type)] = translations;
			}
	
			return acc;
		}, {} as Record<string, Record<string, string>>);
	
	return typeTranslations;
}
	
function getTypeKeyPrefix(type: string | AgencyTypeDto) {
	return `types.${typeof type === "string" ? type : type.Code}`;
}
	
function getTypeTranslations(
	type: AgencyTypeDto,
	language: LanguageSpecifier,
	isDefault: boolean
) {
	//we want to construct an object that looks like this:
	/*
		{
			"name": "Type Code" : from the AgencyTypeDto.translations if defined, else type.Name
		}
		*/
	
	if(!type.Translations?.[language] && !isDefault) {
		return undefined;
	}
	
	return merge(
		{}, 
		(isDefault ? {
			Name: type.Name,
			Labels: {
				Name: type.Name,
				Code: `${type.Name} #`,
				ExternalKey: "External Key",
				Classification: "Classification",
				ExternalKey_other: "External Keys",
				Classification_other: "Classifications"
			}
		} : undefined),
		(type.Translations?.[language] ?? {})
	);
}
